import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiDollarSign, FiUsers, FiShoppingBag, FiRefreshCw, FiTruck } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { startOfDay, startOfWeek, startOfMonth, startOfYear } from 'date-fns';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  overflow-x: auto;
  direction: ltr;

  @media (max-width: 768px) {
    margin: 0;
    padding: 0;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  direction: ltr;

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 4px;
  }
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.$active ? '#5efba9' : '#e5e7eb'};
  border-radius: 8px;
  background: ${props => props.$active ? '#5efba9' : 'white'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '400'};
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$active ? '#4de89e' : '#f9fafb'};
    border-color: ${props => props.$active ? '#4de89e' : '#d1d5db'};
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
  }
`;

const KPICards = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const KPICard = styled.div`
  background: ${props => props.variant === 'dark' ? '#1f2937' : '#5efba9'};
  color: ${props => props.variant === 'dark' ? 'white' : '#1f2937'};
  padding: 22px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 768px) {
    padding: 18px;
  }
`;

const KPIHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const KPIIcon = styled.div`
  width: 38px;
  height: 38px;
  background: ${props => props.variant === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const KPILabel = styled.div`
  font-size: 13px;
  opacity: 0.8;
`;

const KPIValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  .kpiCurrency {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    opacity: 0.85;
    margin-right: 8px;
    vertical-align: baseline;

    @media (max-width: 768px) {
      font-size: 11px;
      margin-right: 6px;
    }
  }
`;

const ProductDataSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatCard = styled.div`
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

export default function AdminDashboard() {
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const [timeFilter, setTimeFilter] = useState('all'); // 'daily', 'weekly', 'monthly', 'yearly', 'all'
  const [dashboardData, setDashboardData] = useState({
    totalSales: 0,
    orderCount: 0,
    customerCount: 0,
    refundCount: 0,
    shippingIncome: 0,
    totalBrands: 0,
    activeBrands: 0,
    totalProducts: 0,
    activeProducts: 0,
    totalUsers: 0,
    productsSold: 0,
    loading: true
  });

  const fetchDashboardData = React.useCallback(async () => {
    if (!session) return;

    try {
      console.log('=== SuperAdmin Dashboard - Fetching System-Wide Data ===');
      
      // Get the start date based on the filter
      const now = new Date();
      let startDate = null;
      switch (timeFilter) {
        case 'daily':
          startDate = startOfDay(now).toISOString();
          break;
        case 'weekly':
          startDate = startOfWeek(now, { weekStartsOn: 0 }).toISOString();
          break;
        case 'monthly':
          startDate = startOfMonth(now).toISOString();
          break;
        case 'yearly':
          startDate = startOfYear(now).toISOString();
          break;
        default:
          startDate = null; // 'all' - no date filter
      }
      
      // Fetch paid orders first (single source of truth for monetary values)
      let paidOrdersQuery = supabase
        .from('orders')
        .select('id, profile_id, status, payment_status, created_at, grand_total, shipping_fee')
        .eq('payment_status', 'paid');

      if (startDate) {
        paidOrdersQuery = paidOrdersQuery.gte('created_at', startDate);
      }

      const { data: paidOrders, error: paidOrdersError } = await paidOrdersQuery;

      console.log('Paid orders fetched:', paidOrders?.length || 0);

      if (paidOrdersError) {
        console.error('Error fetching paid orders:', paidOrdersError);
        setDashboardData(prev => ({ ...prev, loading: false }));
        return;
      }

      const normalizedPaidOrders = (paidOrders || []).map((order) => ({
        ...order,
        _normalizedStatus: String(order?.status || '').trim().toLowerCase()
      }));

      // Exclude canceled orders in-memory.
      const validOrders = normalizedPaidOrders.filter((order) => {
        return order._normalizedStatus !== 'canceled' && order._normalizedStatus !== 'cancelled';
      });

      // Calculate system-wide monetary metrics from orders (exact amounts)
      // Total Sales excludes delivery income (shipping is shown as separate KPI).
      const totalSales = validOrders.reduce((sum, order) => {
        const grandTotal = parseFloat(order.grand_total || 0);
        const shippingFee = parseFloat(order.shipping_fee || 0);
        return sum + (grandTotal - shippingFee);
      }, 0);
      const orderCount = validOrders.length;
      
      // Get unique customers across all brands
      const uniqueCustomers = [...new Set(validOrders.map(order => order.profile_id).filter(Boolean))];
      const customerCount = uniqueCustomers.length;

      // Count canceled paid orders from the same fetched dataset for consistency.
      const canceledCount = normalizedPaidOrders.filter((order) => {
        return order._normalizedStatus === 'canceled' || order._normalizedStatus === 'cancelled';
      }).length;

      // Calculate total shipping income excluding canceled orders
      const shippingIncome = validOrders.reduce((sum, order) => {
        return sum + parseFloat(order.shipping_fee || 0);
      }, 0);

      console.log('Shipping income:', shippingIncome);

      // Fetch brands data
      const { data: brandsData, error: brandsError } = await supabase
        .from('brands')
        .select('id, active');

      if (brandsError) {
        console.error('Error fetching brands:', brandsError);
      }

      const totalBrands = brandsData?.length || 0;
      const activeBrands = brandsData?.filter(b => b.active === true).length || 0;

      console.log('Brands:', { totalBrands, activeBrands });

      // Fetch products data
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id, status');

      if (productsError) {
        console.error('Error fetching products:', productsError);
      }

      const totalProducts = productsData?.length || 0;
      const activeProducts = productsData?.filter((p) => {
        const status = String(p?.status || '').trim().toLowerCase();
        // Different environments have used active/published/available for live products.
        return status === 'active' || status === 'published' || status === 'available';
      }).length || 0;

      console.log('Products:', { totalProducts, activeProducts });

      // Fetch users (profiles) data
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true });

      if (usersError) {
        console.error('Error fetching users:', usersError);
      }

      const totalUsers = usersData?.length || 0;

      console.log('Users:', totalUsers);

      // Fetch paid order items (for quantity-based KPIs)
      let orderItemsQuery = supabase
        .from('order_items')
        .select(`
          quantity,
          orders!inner(
            id,
            status,
            payment_status,
            created_at
          )
        `)
        .eq('orders.payment_status', 'paid');

      if (startDate) {
        orderItemsQuery = orderItemsQuery.gte('orders.created_at', startDate);
      }

      const { data: orderItems, error: orderItemsError } = await orderItemsQuery;

      if (orderItemsError) {
        console.error('Error fetching order items:', orderItemsError);
      }

      const validOrderItems = (orderItems || []).filter((item) => {
        const status = String(item?.orders?.status || '').trim().toLowerCase();
        return status !== 'canceled' && status !== 'cancelled';
      });

      // Calculate products sold (total quantity from non-canceled paid order items)
      const productsSold = validOrderItems.reduce((sum, item) => {
        return sum + (parseInt(item.quantity) || 0);
      }, 0);

      console.log('Dashboard Metrics:', {
        totalSales,
        orderCount,
        customerCount,
        refundCount: canceledCount,
        shippingIncome,
        totalBrands,
        activeBrands,
        totalProducts,
        activeProducts,
        totalUsers,
        productsSold,
      });

      setDashboardData({
        totalSales,
        orderCount,
        customerCount,
        refundCount: canceledCount,
        shippingIncome,
        totalBrands,
        activeBrands,
        totalProducts,
        activeProducts,
        totalUsers,
        productsSold,
        loading: false
      });
    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      setDashboardData(prev => ({ ...prev, loading: false }));
    }
  }, [session, timeFilter]);

  useEffect(() => {
    if (session) {
      fetchDashboardData();
    }
  }, [session, fetchDashboardData]);

  return (
    <DashboardContainer>
      <FilterBar>
        <FilterButtons>
          <FilterButton 
            $active={timeFilter === 'daily'} 
            onClick={() => setTimeFilter('daily')}
          >
            {isArabic ? 'يومي' : 'Daily'}
          </FilterButton>
          <FilterButton 
            $active={timeFilter === 'weekly'} 
            onClick={() => setTimeFilter('weekly')}
          >
            {isArabic ? 'أسبوعي' : 'Weekly'}
          </FilterButton>
          <FilterButton 
            $active={timeFilter === 'monthly'} 
            onClick={() => setTimeFilter('monthly')}
          >
            {isArabic ? 'شهري' : 'Monthly'}
          </FilterButton>
          <FilterButton 
            $active={timeFilter === 'yearly'} 
            onClick={() => setTimeFilter('yearly')}
          >
            {isArabic ? 'سنوي' : 'Yearly'}
          </FilterButton>
          <FilterButton 
            $active={timeFilter === 'all'} 
            onClick={() => setTimeFilter('all')}
          >
            {isArabic ? 'كل الوقت' : 'All Time'}
          </FilterButton>
        </FilterButtons>
      </FilterBar>
      {dashboardData.loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          {isArabic ? 'جارٍ تحميل بيانات لوحة التحكم...' : 'Loading dashboard data...'}
        </div>
      ) : (
        <>
          <KPICards>
            <KPICard>
              <KPIHeader>
                <KPIIcon variant="light">
                  <FiDollarSign />
                </KPIIcon>
                <KPILabel>{isArabic ? 'إجمالي المبيعات (كل العلامات)' : 'Total Sales (All Brands)'}</KPILabel>
              </KPIHeader>
              <KPIValue>
                <span className="kpiCurrency">OMR</span>
                {dashboardData.totalSales.toFixed(2)}
              </KPIValue>
            </KPICard>

            <KPICard variant="dark">
              <KPIHeader>
                <KPIIcon variant="dark">
                  <FiTruck />
                </KPIIcon>
                <KPILabel>{isArabic ? 'دخل الشحن' : 'Shipping Income'}</KPILabel>
              </KPIHeader>
              <KPIValue>
                <span className="kpiCurrency">OMR</span>
                {dashboardData.shippingIncome.toFixed(2)}
              </KPIValue>
            </KPICard>

            <KPICard>
              <KPIHeader>
                <KPIIcon variant="light">
                  <FiUsers />
                </KPIIcon>
                <KPILabel>{isArabic ? 'إجمالي العملاء' : 'Total Customers'}</KPILabel>
              </KPIHeader>
              <KPIValue>{dashboardData.customerCount}</KPIValue>
            </KPICard>

            <KPICard variant="dark">
              <KPIHeader>
                <KPIIcon variant="dark">
                  <FiShoppingBag />
                </KPIIcon>
                <KPILabel>{isArabic ? 'إجمالي الطلبات' : 'Total Orders'}</KPILabel>
              </KPIHeader>
              <KPIValue>{dashboardData.orderCount}</KPIValue>
            </KPICard>

            <KPICard>
              <KPIHeader>
                <KPIIcon variant="light">
                  <FiRefreshCw />
                </KPIIcon>
                <KPILabel>{isArabic ? 'الطلبات الملغاة' : 'Canceled Orders'}</KPILabel>
              </KPIHeader>
              <KPIValue>{dashboardData.refundCount}</KPIValue>
            </KPICard>
          </KPICards>

      <ProductDataSection>
        <SectionHeader>
          <SectionTitle>{isArabic ? 'إحصائيات النظام' : 'System Statistics'}</SectionTitle>
        </SectionHeader>

        <StatsGrid>
          <StatCard>
            <StatLabel>{isArabic ? 'إجمالي العلامات' : 'Total Brands'}</StatLabel>
            <StatValue>{dashboardData.totalBrands}</StatValue>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
              {dashboardData.activeBrands} {isArabic ? 'نشط' : 'active'}
            </div>
          </StatCard>
          <StatCard>
            <StatLabel>{isArabic ? 'إجمالي المنتجات' : 'Total Products'}</StatLabel>
            <StatValue>{dashboardData.totalProducts}</StatValue>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
              {dashboardData.activeProducts} {isArabic ? 'نشط' : 'active'}
            </div>
          </StatCard>
          <StatCard>
            <StatLabel>{isArabic ? 'المنتجات المباعة' : 'Products Sold'}</StatLabel>
            <StatValue>{dashboardData.productsSold}</StatValue>
          </StatCard>
        </StatsGrid>
      </ProductDataSection>
        </>
      )}
    </DashboardContainer>
  );
}
